import { redirect } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "utils/baseUrl";
import { validateToken } from "utils/checkAuthorization";
import { getUserDetails } from "utils/localStorageServices";
import { getSubdomainInfo } from "utils/subdomainDetection";

/**
 * Loader for /login route that redirects already-authenticated users
 * but allows active login flows to proceed
 */
export async function authGuardLoader({ request }: { request: Request }) {
  const url = new URL(request.url);

  // Check if user has valid authentication
  const userDetails = getUserDetails();
  const hasValidToken = userDetails?.token && validateToken();

  // If no valid token, allow access to login page
  if (!hasValidToken) {
    return null;
  }

  // User has valid token - check if they're actively logging in
  const isActiveLoginFlow = checkIfActiveLoginFlow(url);

  if (isActiveLoginFlow) {
    // User is in the middle of login flow, let it complete
    return null;
  }

  // Check if user is trying to access a public speaker page
  const isSpeakerPageAccess = checkIfSpeakerPageAccess(url);

  if (isSpeakerPageAccess) {
    // Validate that the speaker actually exists
    await validateSpeakerExists(url);
    return null;
  }

  // User is already logged in and NOT actively logging in
  // Redirect them to the app
  return redirect("/overview");
}

function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage errors in restricted iframe contexts.
  }
}

/**
 * Determines if user is actively going through login flow
 */
function checkIfActiveLoginFlow(url: URL): boolean {
  // Check for flow state in URL (email/otp/password flows)
  const flowState = url.searchParams.get("s");
  if (flowState) return true;

  // Check for OAuth callback
  const oauthCode = url.searchParams.get("code");
  if (oauthCode) return true;

  // Check for special redirect params (Zoom integration, etc)
  const hasRedirectParams = url.searchParams.get("redirect");
  if (hasRedirectParams) return true;

  // Check localStorage for in-progress auth session
  const authResponse = safeLocalStorageGet("auth_verification_response");
  if (authResponse) {
    try {
      const parsed = JSON.parse(authResponse);
      // If we have an email verified but haven't completed login yet
      if (parsed.email && parsed.reference) {
        return true;
      }
    } catch (e) {
      // Invalid JSON, clean it up
      safeLocalStorageRemove("auth_verification_response");
    }
  }

  return false;
}

/**
 * Determines if user is trying to access a public speaker page
 */
function checkIfSpeakerPageAccess(url: URL): boolean {
  const subdomainInfo = getSubdomainInfo();
  const pathname = url.pathname;

  // Check if accessing via username subdomain at root path (e.g., nana24.tweek.io/)
  if (subdomainInfo.isUserSubdomain && pathname === "/") {
    return true;
  }

  // Check if accessing via username query parameter in dev mode (e.g., /?username=nana24)
  if (url.searchParams.has("username")) {
    return true;
  }

  return false;
}

/**
 * Validates that the speaker exists by calling the speaker API.
 * Throws a 404 Response if the speaker is not found.
 */
export async function validateSpeakerExists(url: URL): Promise<void> {
  const subdomainInfo = getSubdomainInfo();
  const username =
    subdomainInfo.isUserSubdomain && subdomainInfo.username
      ? subdomainInfo.username
      : url.searchParams.get("username");

  if (!username) {
    throw new Response("Speaker not found", { status: 404 });
  }

  try {
    const result = await axios.get(`${baseUrl}/speaker/${username}`);
    const data = result.data;

    // If no meaningful speaker data returned, treat as not found
    if (!data || (!data.name && !data.designation && !data.location && !data.company)) {
      throw new Response("Speaker not found", { status: 404 });
    }
  } catch (error) {
    if (error instanceof Response) {
      throw error;
    }
    throw new Response("Speaker not found", { status: 404 });
  }
}
